// SPDX-License-Identifier: SEE LICENSE
//
// Dashboard auth context. The real protection is server-side (the backend
// rejects /v1/sessions* without a valid login cookie); this gate is the UX
// layer that shows the login page until a session exists.
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

import { getAuthSession, loginDashboard, logoutDashboard } from './api';
import { Login } from './pages/Login';

interface AuthState {
  ready: boolean;
  authed: boolean;
  user?: string | undefined;
  enforced?: boolean | undefined;
}

interface AuthContextValue {
  state: AuthState;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ ready: false, authed: false });

  const refresh = useCallback(() => {
    getAuthSession()
      .then((s) => setState({ ready: true, authed: !!s.authed, user: s.user, enforced: s.enforced }))
      .catch(() => setState({ ready: true, authed: false }));
  }, []);

  useEffect(() => refresh(), [refresh]);

  const login = useCallback(async (username: string, password: string) => {
    const user = await loginDashboard(username, password);
    setState({ ready: true, authed: true, user, enforced: true });
  }, []);

  const logout = useCallback(async () => {
    await logoutDashboard();
    setState({ ready: true, authed: false });
  }, []);

  return <AuthContext.Provider value={{ state, login, logout }}>{children}</AuthContext.Provider>;
}

/** Gate a route behind authentication. Shows a loader while resolving, the
 *  login page when unauthenticated, and the children once authenticated. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { state } = useAuth();
  if (!state.ready) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#04090f',
          color: '#6f878e',
          fontSize: 13,
          letterSpacing: 1,
        }}
      >
        <span className="animate-livepulse">Authenticating…</span>
      </div>
    );
  }
  if (!state.authed) return <Login />;
  return <>{children}</>;
}
